import React from 'react';
import { readDate } from '../../utils/appDate';
import { T, NUMERIC, money } from './customers.theme';
import { Dialog, DialogHeader, ErrorLine, GhostButton, Spinner } from './CustomersUI';

/** Copy lives in constants rather than inline in the markup. */
const COPY = {
  title: 'Are you sure?',
  cancel: 'Cancel',
  confirm: 'Delete',
  failed: 'Could not delete. Try again.',
  becomes: (name) => `${name}'s balance becomes`,
};

/**
 * Confirm before a line leaves the book.
 *
 * It shows the line itself — description, date, amount — because "are you
 * sure?" is not a question anyone can answer without seeing what they clicked.
 *
 * Nothing is really destroyed: the server marks the row deleted and cuts it
 * loose from the customer rather than dropping it, and puts back exactly the
 * amount it had moved. It stops appearing because the read filters deleted
 * rows out.
 */
export default function DeleteTransactionDialog({ transaction, customer, currency, onClose, onConfirm }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const isCharge = Number(transaction.debit || 0) > 0;
  const amount = isCharge ? transaction.debit : transaction.credit;

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm(transaction);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={400}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.title} onClose={onClose} locked={busy} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginTop: 18,
            padding: '14px 16px',
            background: T.sunk,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusControl,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
              {transaction.description || '—'}
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: isCharge ? T.brand : T.paid,
                ...NUMERIC,
              }}
            >
              {money(amount, currency)}
            </span>
          </div>
          <span style={{ fontSize: 12, color: T.inkFaint, ...NUMERIC }}>
            {readDate(transaction.date).format('DD/MMM/YYYY')}
          </span>
        </div>

        <div style={{ fontSize: 12.5, color: T.inkSoft, lineHeight: 1.6, paddingTop: 16 }}>
          {COPY.becomes(customer?.name)}{' '}
          <strong style={{ fontWeight: 700, color: T.ink, ...NUMERIC }}>
            {money(
              Number(customer?.balance || 0) -
                (Number(transaction.debit || 0) - Number(transaction.credit || 0)),
              currency
            )}
          </strong>
          .
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: error ? 12 : 20 }}>
          <GhostButton style={{ flex: 1 }} onClick={onClose} disabled={busy}>
            {COPY.cancel}
          </GhostButton>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            style={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              height: 48,
              background: T.surface,
              border: `1px solid ${T.dangerBorder}`,
              borderRadius: 10,
              font: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              color: T.danger,
              opacity: busy ? 0.6 : 1,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? <Spinner size={14} /> : null}
            <span>{COPY.confirm}</span>
          </button>
        </div>
      </div>
    </Dialog>
  );
}
